import { SKILL_BRANCH_NAMES, SKILL_BRANCHES, SKILL_CATALOG, SKILL_GROUPS, type SkillBranch, type Vector2 } from '@sorrem/shared';
import { polar, toRadians } from './skill-tree-math';

/**
 * Where everything on the skill tree sits, in tree units (y grows downward,
 * like the DOM). A node is 120 units wide. See docs/gameplay/skill-tree-ui.md
 * (Layout).
 *
 * Positions come from the skill catalog (`@sorrem/shared`): each branch fans
 * out from its dial across a sector, each group takes a share of that sector
 * sized by its skills, its grouping root sits at the middle of its share,
 * and its skills sit on an outer ring. The same catalog always gives the
 * same positions, so a player's mental map never shifts; a new skill in the
 * catalog makes room for itself.
 */

export interface SkillTreeNodePlacement {
    /** The skill id (e.g. `blade-mastery`). */
    readonly id: string;
    readonly branch: SkillBranch;
    /** The grouping root it hangs from. */
    readonly group: string;
    /** Display name used until the character's skill data supplies one. */
    readonly name: string;
    readonly position: Vector2;
}

/** A grouping root: a named junction between a dial and its skills, with no progress of its own. */
export interface SkillTreeGroupPlacement {
    readonly id: string;
    readonly branch: SkillBranch;
    readonly name: string;
    readonly position: Vector2;
}

export interface SkillTreeDialPlacement {
    readonly branch: SkillBranch;
    readonly name: string;
    readonly position: Vector2;
}

/** Diameters in tree units. */
export const SKILL_TREE_NODE_SIZE = 120;
export const SKILL_TREE_GROUP_SIZE = 84;
export const SKILL_TREE_DIAL_SIZE = 160;
export const SKILL_TREE_SIGIL_SIZE = 240;
/** Room below a node, group, or dial for its name label. */
export const SKILL_TREE_LABEL_SPACE = 56;

export const SKILL_TREE_SIGIL_POSITION: Vector2 = { x: 0, y: 0 };

interface BranchFan {
    /** The direction the branch grows, in degrees clockwise from +x (so -90 is up). */
    readonly angle: number;
    /** How wide its skills fan out around its dial, in degrees. */
    readonly sector: number;
    /** From the dial to its grouping roots, and to its skills (two rings, alternating, where skills would crowd). */
    readonly groupRadius: number;
    readonly skillRadius: number;
    readonly skillRadiusOuter: number;
}

/** Martial grows up, Essence to the lower left, Crafting to the lower right. Essence, with the most groups, reaches farthest. */
const BRANCH_FANS: Record<SkillBranch, BranchFan> = {
    martial: { angle: -90, sector: 150, groupRadius: 340, skillRadius: 700, skillRadiusOuter: 880 },
    essence: { angle: 150, sector: 175, groupRadius: 470, skillRadius: 830, skillRadiusOuter: 1010 },
    crafting: { angle: 30, sector: 110, groupRadius: 340, skillRadius: 700, skillRadiusOuter: 880 },
};

/** From the sigil to each dial. */
const DIAL_DISTANCE = 380;
/** Skills closer than this along one ring alternate between the two rings. */
const SKILL_CROWDING = 230;
/** Space between neighboring groups, in skill widths. */
const GROUP_GAP = 0.6;

export const SKILL_TREE_DIALS: readonly SkillTreeDialPlacement[] = SKILL_BRANCHES.map((branch) => ({
    branch,
    name: SKILL_BRANCH_NAMES[branch],
    position: polar(SKILL_TREE_SIGIL_POSITION, DIAL_DISTANCE, BRANCH_FANS[branch].angle),
}));

function layoutBranch(branch: SkillBranch): { groups: SkillTreeGroupPlacement[]; nodes: SkillTreeNodePlacement[] } {
    const fan = BRANCH_FANS[branch];
    const dial = SKILL_TREE_DIALS.find((candidate) => candidate.branch === branch)!.position;
    const groups = SKILL_GROUPS.filter((group) => group.branch === branch);
    const skillsByGroup = groups.map((group) => SKILL_CATALOG.filter((entry) => entry.group === group.id));
    const units = skillsByGroup.reduce((total, skills) => total + skills.length, 0) + GROUP_GAP * (groups.length - 1);
    const unitAngle = fan.sector / units;
    const stagger = 2 * fan.skillRadius * Math.sin(toRadians(unitAngle / 2)) < SKILL_CROWDING;

    const groupPlacements: SkillTreeGroupPlacement[] = [];
    const nodePlacements: SkillTreeNodePlacement[] = [];
    let cursor = fan.angle - fan.sector / 2;
    let skillIndex = 0;
    groups.forEach((group, groupIndex) => {
        const skills = skillsByGroup[groupIndex]!;
        const start = cursor;
        skills.forEach((entry) => {
            const radius = stagger && skillIndex % 2 === 1 ? fan.skillRadiusOuter : fan.skillRadius;
            nodePlacements.push({
                id: entry.id,
                branch,
                group: group.id,
                name: entry.name,
                position: polar(dial, radius, cursor + unitAngle / 2),
            });
            cursor += unitAngle;
            skillIndex += 1;
        });
        groupPlacements.push({ id: group.id, branch, name: group.name, position: polar(dial, fan.groupRadius, (start + cursor) / 2) });
        cursor += unitAngle * GROUP_GAP;
    });
    return { groups: groupPlacements, nodes: nodePlacements };
}

const LAYOUTS = SKILL_BRANCHES.map(layoutBranch);

export const SKILL_TREE_GROUPS: readonly SkillTreeGroupPlacement[] = LAYOUTS.flatMap((layout) => layout.groups);
export const SKILL_TREE_NODES: readonly SkillTreeNodePlacement[] = LAYOUTS.flatMap((layout) => layout.nodes);

export interface SkillTreeBounds {
    readonly min: Vector2;
    readonly max: Vector2;
}

/** The box around everything on the tree, labels included, in tree units. */
export function resolveSkillTreeBounds(): SkillTreeBounds {
    const boxes: { position: Vector2; radius: number; below: number }[] = [
        { position: SKILL_TREE_SIGIL_POSITION, radius: SKILL_TREE_SIGIL_SIZE / 2, below: 0 },
        ...SKILL_TREE_DIALS.map((dial) => ({ position: dial.position, radius: SKILL_TREE_DIAL_SIZE / 2, below: SKILL_TREE_LABEL_SPACE })),
        ...SKILL_TREE_GROUPS.map((group) => ({ position: group.position, radius: SKILL_TREE_GROUP_SIZE / 2, below: SKILL_TREE_LABEL_SPACE })),
        // Node labels can be wider than the node; give them room either side.
        ...SKILL_TREE_NODES.map((node) => ({ position: node.position, radius: SKILL_TREE_NODE_SIZE * 0.75, below: SKILL_TREE_LABEL_SPACE })),
    ];
    return {
        min: {
            x: Math.min(...boxes.map((box) => box.position.x - box.radius)),
            y: Math.min(...boxes.map((box) => box.position.y - box.radius)),
        },
        max: {
            x: Math.max(...boxes.map((box) => box.position.x + box.radius)),
            y: Math.max(...boxes.map((box) => box.position.y + box.radius + box.below)),
        },
    };
}

export const SKILL_TREE_BOUNDS: SkillTreeBounds = resolveSkillTreeBounds();
